import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Divider,
  Switch,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountTree as TreeIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import TypificationTreeBuilder from '../components/ends/TypificationTreeBuilder';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ends-tabpanel-${index}`}
      aria-labelledby={`ends-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const EndsPage = () => {
  const { ends } = useApi();
  const { showSuccess, showError, showWarning } = useNotification();
  const { canAction } = usePermissions();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Group Dialog States
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    max_levels: 3
  });

  // Load groups
  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await ends.getGroups({ include_inactive: true });
      if (response.success) {
        setGroups(response.data || []);
      } else {
        showError('Error al cargar grupos de finales');
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      showError('Error al cargar grupos de finales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  // Group Management Functions
  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '', max_levels: 3 });
    setGroupDialogOpen(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      max_levels: group.max_levels
    });
    setGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    try {
      if (!groupForm.name.trim()) {
        showError('El nombre es requerido');
        return;
      }

      let response;
      if (editingGroup) {
        response = await ends.updateGroup(editingGroup.id, groupForm);
      } else {
        response = await ends.createGroup(groupForm);
      }

      if (response.success) {
        showSuccess(editingGroup ? 'Grupo actualizado' : 'Grupo creado');
        setGroupDialogOpen(false);
        loadGroups();
      } else {
        showError(response.error || 'Error al guardar grupo');
      }
    } catch (error) {
      console.error('Error saving group:', error);
      showError('Error al guardar grupo');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`¿Estás seguro de eliminar el grupo "${group.name}"?`)) {
      return;
    }

    try {
      const response = await ends.deleteGroup(group.id);
      if (response.success) {
        showSuccess('Grupo eliminado');
        loadGroups();
      } else {
        // Si hay skills usando este grupo, mostrar información detallada
        if (response.skills_using && response.skills_using.length > 0) {
          const skillsList = response.skills_using
            .map(skill => `• ${skill.skill_name} (${skill.skill_def}) - Final: ${skill.final_code}`)
            .join('\n');

          const message = `${response.error}\n\nSkills afectados:\n${skillsList}\n\nEdite los siguientes skills para cambiar su configuración de finales antes de eliminar este grupo.`;

          // Mostrar alerta con información detallada
          showWarning(`${response.error}. Verifique los skills asociados antes de eliminar.`);
        } else {
          showError(response.error || 'Error al eliminar grupo');
        }
      }
    } catch (error) {
      console.error('Error deleting group:', error);

      // Manejar errores HTTP 400 que vienen del backend
      if (error.response && error.response.status === 400 && error.response.data) {
        const data = error.response.data;
        if (data.skills_using && data.skills_using.length > 0) {
          const skillsList = data.skills_using
            .map(skill => `• ${skill.skill_name} (${skill.skill_def}) - Final: ${skill.final_code}`)
            .join('\n');

          const message = `${data.error}\n\nSkills afectados:\n${skillsList}\n\nEdite los siguientes skills para cambiar su configuración de finales antes de eliminar este grupo.`;

          showWarning(`${data.error}. Verifique los skills asociados antes de eliminar.`);
          return;
        }
      }

      showError('Error al eliminar grupo');
    }
  };

  const handleToggleGroupActive = async (group, nextActive) => {
    try {
      const isActive = typeof nextActive === 'boolean' ? nextActive : group.is_active === false;
      const resolvedActive = typeof nextActive === 'boolean' ? nextActive : isActive;
      const response = await ends.updateGroup(group.id, { is_active: resolvedActive });
      if (response.success) {
        showSuccess(resolvedActive ? 'Grupo activado' : 'Grupo desactivado');
        loadGroups();
      } else {
        showError(response.error || 'Error al actualizar grupo');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      showError('Error al actualizar grupo');
    }
  };

  const handleManageTypifications = (group) => {
    setSelectedGroup(group);
    setTabValue(1);
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Finales
        </Typography>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Finales
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
        Gestión de grupos de finales y tipificaciones jerárquicas
      </Typography>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Grupos de Finales" />
        <Tab
          label={`Tipificaciones${selectedGroup ? ` - ${selectedGroup.name}` : ''}`}
          disabled={!selectedGroup}
        />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Grupos de Finales ({groups.length})
          </Typography>
          {canAction('create_end_group') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateGroup}
            >
              Nuevo Grupo
            </Button>
          )}
        </Box>

        {groups.length === 0 ? (
          <Alert severity="info">
            No hay grupos de finales creados. Crea el primer grupo para comenzar.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {groups.map((group) => (
              <Grid item xs={12} md={6} lg={4} key={group.id}>
                <Card
                  sx={{
                    opacity: group.is_active === false ? 0.7 : 1,
                    borderColor: group.is_active === false ? 'grey.300' : 'divider'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {group.name}
                      </Typography>
                      <Tooltip title={group.is_active === false ? 'Activar grupo' : 'Desactivar grupo'}>
                        <Switch
                          size="small"
                          checked={group.is_active !== false}
                          onChange={(e) => handleToggleGroupActive(group, e.target.checked)}
                          inputProps={{ 'aria-label': 'Activar/desactivar grupo' }}
                        />
                      </Tooltip>
                      {canAction('edit_end_group') && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditGroup(group)}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canAction('delete_end_group') && (
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleDeleteGroup(group)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    {group.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {group.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        label={`${group.max_levels} niveles`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`${group.typifications_count || 0} tipificaciones`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                      {group.is_active === false && (
                        <Chip
                          label="Inactivo"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<TreeIcon />}
                      onClick={() => handleManageTypifications(group)}
                    >
                      Gestionar Tipificaciones
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {selectedGroup ? (
          <TypificationTreeBuilder
            group={selectedGroup}
            onTreeUpdate={loadGroups}
          />
        ) : (
          <Alert severity="info">
            Selecciona un grupo desde la pestaña anterior para gestionar sus tipificaciones.
          </Alert>
        )}
      </TabPanel>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre *"
            fullWidth
            variant="outlined"
            value={groupForm.name}
            onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descripción"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={groupForm.description}
            onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Niveles máximos"
            select
            fullWidth
            variant="outlined"
            value={groupForm.max_levels}
            onChange={(e) => setGroupForm({ ...groupForm, max_levels: parseInt(e.target.value) })}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
              <MenuItem key={level} value={level}>
                {level} {level === 1 ? 'nivel' : 'niveles'}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSaveGroup} variant="contained">
            {editingGroup ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EndsPage;
